import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Link2, Link2Off, Search, Star, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { experiencesApi } from '@/api';

const LinkEventsModal = ({ isOpen, onClose, linkedEventIds = [], onEventsUpdated }) => {
  const { i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkingEventId, setLinkingEventId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchLocalEvents();
    }
  }, [isOpen]);

  const fetchLocalEvents = async () => {
    setLoading(true);
    try {
      const result = await experiencesApi.getLocalEvents({ limit: 50 });
      setEvents(result.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkEvent = async (eventId) => {
    setLinkingEventId(eventId);
    try {
      await experiencesApi.linkEventToGuide(eventId);
      toast.success(i18n.language === 'fr' ? 'Événement lié !' : 'Event linked!');
      onEventsUpdated?.();
    } catch (error) {
      const message = error.response?.data?.detail || (i18n.language === 'fr' ? 'Erreur' : 'Error');
      toast.error(message);
    } finally {
      setLinkingEventId(null);
    }
  };

  const handleUnlinkEvent = async (eventId) => {
    setLinkingEventId(eventId);
    try {
      await experiencesApi.unlinkEventFromGuide(eventId);
      toast.success(i18n.language === 'fr' ? 'Événement délié' : 'Event unlinked');
      onEventsUpdated?.();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLinkingEventId(null);
    }
  };

  const filteredEvents = events.filter(event => 
    event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLinked = (eventId) => linkedEventIds.includes(eventId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="link-events-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="w-6 h-6 text-purple-500" />
            {i18n.language === 'fr' ? 'Lier des événements locaux' : 'Link local events'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-gray-600 mt-2">
          {i18n.language === 'fr' 
            ? 'Ajoutez des événements locaux à votre profil pour montrer aux voyageurs ce qui se passe dans votre région.'
            : 'Add local events to your profile to show travelers what\'s happening in your area.'}
        </p>

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={i18n.language === 'fr' ? 'Rechercher un événement...' : 'Search events...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-events-input"
          />
        </div>

        {/* Events List */}
        <div className="flex-1 overflow-y-auto mt-4 space-y-3 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {i18n.language === 'fr' ? 'Aucun événement trouvé' : 'No events found'}
            </div>
          ) : (
            filteredEvents.map((event) => {
              const linked = isLinked(event.experience_id);
              const isProcessing = linkingEventId === event.experience_id;

              return (
                <div 
                  key={event.experience_id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    linked 
                      ? 'border-purple-300 bg-purple-50' 
                      : 'border-gray-200 hover:border-purple-200'
                  }`}
                  data-testid={`event-item-${event.experience_id}`}
                >
                  {/* Image */}
                  <img 
                    src={event.images?.[0] || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=200'}
                    alt={event.title}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {event.is_premium && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full">
                          <Star className="w-3 h-3" fill="white" />
                          {i18n.language === 'fr' ? 'À la une' : 'Featured'}
                        </span>
                      )}
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        event.categories?.includes('healing') || event.category === 'healing'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {event.categories?.includes('healing') || event.category === 'healing'
                          ? (i18n.language === 'fr' ? 'Retraite' : 'Retreat')
                          : (i18n.language === 'fr' ? 'Événement' : 'Event')}
                      </span>
                    </div>

                    <h4 className="font-semibold text-gray-900 truncate">{event.title}</h4>
                    
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </span>
                      {event.price > 0 ? (
                        <span className="font-medium text-orange-600">{event.price}€</span>
                      ) : (
                        <span className="text-green-600 font-medium">
                          {i18n.language === 'fr' ? 'Gratuit' : 'Free'}
                        </span>
                      )}
                    </div>

                    {event.organizer_name && (
                      <p className="text-xs text-gray-400 mt-1">
                        {i18n.language === 'fr' ? 'Par' : 'By'} {event.organizer_name}
                      </p>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="flex-shrink-0">
                    {linked ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnlinkEvent(event.experience_id)}
                        disabled={isProcessing}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`unlink-event-${event.experience_id}`}
                      >
                        {isProcessing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Link2Off className="w-4 h-4 mr-1" />
                            {i18n.language === 'fr' ? 'Délier' : 'Unlink'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleLinkEvent(event.experience_id)}
                        disabled={isProcessing}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid={`link-event-${event.experience_id}`}
                      >
                        {isProcessing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Link2 className="w-4 h-4 mr-1" />
                            {i18n.language === 'fr' ? 'Lier' : 'Link'}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {linkedEventIds.length} {i18n.language === 'fr' ? 'événement(s) lié(s)' : 'event(s) linked'}
          </p>
          <Button variant="outline" onClick={onClose}>
            {i18n.language === 'fr' ? 'Fermer' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkEventsModal;
